import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { featuredArticlePath, resolveContentImageUrl, stripHtml } from '@/lib/contentImage';
import { pickFeaturedLearning } from '@/lib/demoArticle';
import { advertisementHref, journeyDayForMember } from '@shared/imageArticle';

function todayKey() {
  return new Date().toLocaleDateString('en-CA');
}

type ContentItem = {
  id?: string;
  headline?: string;
  title?: string;
  subHeading?: string;
  bodyText?: string;
  text?: string;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  headerImage?: string | null;
  isFeatured?: boolean;
  isPublished?: boolean;
  isActive?: boolean;
  featureDay?: number | null;
  sequence?: 'journey' | 'shared';
  linkUrl?: string | null;
  companyId?: string | null;
};

/**
 * Homepage popup. Prefers image-led motivational messages (headlines in the image);
 * falls back to featured learning content.
 */
export default function FeaturedArticleOverlay() {
  const [open, setOpen] = useState(false);

  const { data: messages } = useQuery<ContentItem[]>({
    queryKey: ['/api/content/messages'],
    retry: false,
  });

  const { data: learning } = useQuery<ContentItem[]>({
    queryKey: ['/api/content/learning'],
    retry: false,
  });

  const { data: profile } = useQuery<{ createdAt?: string; profileCreatedAt?: string | null }>({
    queryKey: ['/api/profile'],
    retry: false,
  });

  const featured = useMemo(() => {
    const journeyDay = journeyDayForMember(profile?.profileCreatedAt || profile?.createdAt);
    const article = pickFeaturedLearning(Array.isArray(learning) ? learning : [], journeyDay);
    const articleImage = resolveContentImageUrl(article);
    const headline = article ? article.headline || article.title || "Today's featured read" : '';

    // Prefer the featured blog when it has a thumbnail so the popup matches
    // the home ticker and content page.
    if (article && articleImage) {
      return {
        id: article.id || 'featured-article',
        kind: 'article' as const,
        imageUrl: articleImage,
        headline: '',
        altText: headline,
        preview: '',
        link: featuredArticlePath(article.id),
      };
    }

    const imageMsg = Array.isArray(messages)
      ? messages.find((m) => m.isActive !== false && m.imageUrl)
      : null;
    if (imageMsg) {
      // Image-led popups: headline lives in the artwork; keep text only as alt fallback.
      return {
        id: imageMsg.id || 'popup-image',
        kind: 'message' as const,
        imageUrl: imageMsg.imageUrl!,
        headline: '',
        altText: imageMsg.text?.trim() || 'Announcement',
        preview: '',
        link: advertisementHref(imageMsg.linkUrl) || '',
      };
    }

    if (!article) return null;

    return {
      id: article.id || 'featured-article',
      kind: 'article' as const,
      imageUrl: null,
      headline,
      altText: headline,
      preview:
        article.subHeading ||
        stripHtml(article.bodyText).slice(0, 160) ||
        'Tap below to read the full article.',
      link: featuredArticlePath(article.id),
    };
  }, [messages, learning, profile]);

  const storageKey = featured
    ? `swyg-featured-overlay-${todayKey()}-${featured.id}`
    : `swyg-featured-overlay-${todayKey()}`;

  useEffect(() => {
    if (!featured) return;
    if (localStorage.getItem(storageKey)) return;
    const timer = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(timer);
  }, [featured, storageKey]);

  const dismiss = () => {
    localStorage.setItem(storageKey, '1');
    setOpen(false);
  };

  if (!featured) return null;

  const imageLed = Boolean(featured.imageUrl);
  const a11yTitle =
    featured.altText ||
    featured.headline ||
    (featured.kind === 'message' ? 'Announcement' : 'Featured article');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dismiss()}>
      {/*
        Do NOT pass `relative` onto DialogContent — twMerge drops `fixed` and the
        dialog ends up bottom-right on desktop instead of centered.
      */}
      <DialogContent
        hideCloseButton
        className="bg-cream border-deep-200 rounded-3xl max-w-sm p-0 gap-0 overflow-hidden max-h-[90vh]"
      >
        <DialogTitle className="sr-only">{a11yTitle}</DialogTitle>
        <DialogDescription className="sr-only">
          {imageLed
            ? 'Image announcement. Use the buttons below to continue or dismiss.'
            : featured.preview || 'Featured content for today.'}
        </DialogDescription>

        <div className="relative">
          {!imageLed ? (
            <div className="bg-lime-500 px-5 py-3 flex items-center justify-between gap-3">
              <p className="text-xs font-bold tracking-wider text-deep-800 min-w-0 truncate">
                {featured.kind === 'message' ? 'ANNOUNCEMENT' : 'FEATURED ARTICLE'}
              </p>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-full min-w-11 min-h-11 flex items-center justify-center p-2 hover:bg-lime-400/80 transition-colors shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5 text-deep-800" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={dismiss}
              className="absolute right-2 top-2 z-20 rounded-full min-w-11 min-h-11 flex items-center justify-center p-2 bg-black/40 hover:bg-black/55 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          )}

          {featured.imageUrl ? (
            <div className="w-full bg-deep-100 max-h-[70vh] overflow-hidden">
              <img
                src={featured.imageUrl}
                alt={featured.altText || featured.headline || 'Announcement'}
                className="w-full max-h-[70vh] object-contain"
              />
            </div>
          ) : null}

          {imageLed ? (
            <div className="px-5 py-4 space-y-2">
              {featured.kind === 'article' ? (
                <Link href={featured.link}>
                  <Button
                    onClick={dismiss}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-5 font-semibold shadow-[0_4px_0_hsl(var(--deep-800))]"
                  >
                    Read Article
                  </Button>
                </Link>
              ) : featured.link ? (
                featured.link.startsWith('http') ? (
                  <a href={featured.link} target="_blank" rel="noopener noreferrer">
                    <Button
                      type="button"
                      onClick={dismiss}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-5 font-semibold"
                    >
                      Open
                    </Button>
                  </a>
                ) : (
                  <Link href={featured.link}>
                    <Button
                      type="button"
                      onClick={dismiss}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-5 font-semibold"
                    >
                      Open
                    </Button>
                  </Link>
                )
              ) : (
                <Button
                  type="button"
                  onClick={dismiss}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-5 font-semibold"
                >
                  Got it
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                onClick={dismiss}
                className="w-full rounded-full text-deep-500 hover:text-deep-700"
              >
                Maybe later
              </Button>
            </div>
          ) : (
            <div className="px-5 pt-4 pb-5 space-y-4 overflow-y-auto">
              {featured.headline ? (
                <h2 className="text-xl font-extrabold text-deep-800 leading-snug pr-1 break-words">
                  {featured.headline}
                </h2>
              ) : null}
              {featured.preview ? (
                <p className="text-sm text-deep-500 leading-relaxed line-clamp-4 break-words">
                  {featured.preview}
                </p>
              ) : null}

              <div className="flex flex-col gap-2 pt-1">
                {featured.kind === 'article' ? (
                  <Link href={featured.link}>
                    <Button
                      onClick={dismiss}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-5 font-semibold shadow-[0_4px_0_hsl(var(--deep-800))]"
                    >
                      Read Article
                    </Button>
                  </Link>
                ) : (
                  <Button
                    type="button"
                    onClick={dismiss}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-5 font-semibold shadow-[0_4px_0_hsl(var(--deep-800))]"
                  >
                    Got it
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={dismiss}
                  className="w-full rounded-full text-deep-500 hover:text-deep-700"
                >
                  Maybe later
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
